import { Box, Button, Card, Chip, createTheme, Divider, Grid, ThemeProvider, Typography, useMediaQuery, useTheme, Menu, MenuItem } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { esES, ptBR } from '@mui/material/locale';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { styled } from 'styled-components';
import { PaperRelatorio } from '..';
import {
  IComandaEtapaReunioes,
  IReuniaoInterna,
  StatusReuniaoEnum,
  StatusValidacaoEnum,
} from '../../../../commons/types/crm';
import { useTranslation } from '../../../../commons/useTranslasion';
import { useDateFormatters } from '../../../../utils/useDateFormatters';
import { exportToCSV } from '../../../../utils/exportToCSV';
import { exportToXLSX } from '../../../../utils/exportToXLSX';
import { handleStatus } from '../../../Dashboard/TableReunioes';
import { useBuActions } from '../../../../states/config/bu/bu.actions';
import { IBU } from '../../../../commons/types/config';

const formatCurrency = (value: any) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

interface ITimesheetResultado {
  dataInicio: Date;
  dataFim: Date;
  resultado: IComandaEtapaReunioes[];
  reunioesInternas: IReuniaoInterna[];
  onRenderComplete?: () => void;
}

interface IReunioesTabela {
  id: number;
  unidade: string;
  assessorado: string;
  etapa: string;
  dtPrevista: Date | string;
  dtRealizadaInicio: Date | string;
  dtRealizadaFim: Date | string;
  bu: string;
  status: StatusReuniaoEnum;
  statusValidacao: StatusValidacaoEnum;
  valorHora: number;
  reuniaoInterna: boolean;
}

const TimesheetRestultado: React.FC<ITimesheetResultado> = (props) => {
  const { resultado, dataInicio, dataFim, reunioesInternas, onRenderComplete } = props;
  const { translate, language } = useTranslation();
  const { formatarData, formatarHora } = useDateFormatters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lang = language ?? "pt-br";
  let locale = ptBR;

  // Estados para paginação mobile
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const buActions = useBuActions();
  const [buList, setBUList] = useState<IBU[]>([]);

  const getBus = () => {
    buActions
      .list()
      .then((resp: IBU[]) => setBUList(resp))
      .catch((err: any) => toast.warn(err));
  };

  useEffect(() => {
    getBus();
  }, []);

  // Notificar o componente pai quando a renderização for concluída
  useEffect(() => {
    // Usar requestAnimationFrame para garantir que a UI está atualizada
    const renderTimer = setTimeout(() => {
      if (onRenderComplete) {
        onRenderComplete();
      }
    }, 500);

    return () => clearTimeout(renderTimer);
  }, [resultado, reunioesInternas, onRenderComplete]);
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  const [reunioesTabela, setReunioesTabela] = useState<IReunioesTabela[]>([]);
  const [totalHoras, setTotalHoras] = useState(0);
  const [totalReceber, setTotalReceber] = useState(0);
  const [displayedRows, setDisplayedRows] = useState<IReunioesTabela[]>([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    let ttReceber = 0;
    let ttHoras = reunioesTabela.reduce((total, item) => {
      const inicio = moment(item.dtRealizadaInicio as any);
      const fim = moment(item.dtRealizadaFim as any);
      const duration = moment.duration(fim.diff(inicio));
      const horas = duration.asHours();

      if (item.statusValidacao === StatusValidacaoEnum.APROVADO)
        ttReceber += item.valorHora! * horas;

      return total + horas;
    }, 0);

    setTotalHoras(ttHoras);
    setTotalReceber(ttReceber);
  }, [reunioesTabela]);

  useEffect(() => {
    // Converte as strings de data para objetos Date no momento do processamento
    const resultadoTabela: IReunioesTabela[] = resultado.map((r: any) => {
      // Converte datas em formato string para objetos Date
      const dtPrevista = r.dtPrevista ? new Date(r.dtPrevista) : new Date();
      const dtRealizadaInicio = r.dtRealizadaInicio ? new Date(r.dtRealizadaInicio) : new Date();
      const dtRealizadaFim = r.dtRealizadaFim ? new Date(r.dtRealizadaFim) : new Date();

      return {
        id: r.id,
        unidade: r.comandaIdNavigation.modeloComercial,
        assessorado: r.comandaIdNavigation.contato.nome,
        etapa: r.tbEtapaIdNavigation.nome,
        dtPrevista: dtPrevista,
        dtRealizadaInicio: dtRealizadaInicio,
        dtRealizadaFim: dtRealizadaFim,
        bu: buList.find(b => b.id === r.comandaIdNavigation.idBU)?.nome ?? '',
        status: r.status,
        statusValidacao: r.statusValidacao,
        valorHora: r.valorHora,
        reuniaoInterna: false,
      };
    });

    const internasTabela: IReunioesTabela[] = reunioesInternas.map((r: any) => {
      // Converte datas em formato string para objetos Date
      const dtPrevista = r.dtPrevista ? new Date(r.dtPrevista) : new Date();
      const dtRealizadaInicio = r.dtRealizadaInicio ? new Date(r.dtRealizadaInicio) : new Date();
      const dtRealizadaFim = r.dtRealizadaFim ? new Date(r.dtRealizadaFim) : new Date();

      return {
        id: r.id,
        unidade: r.modeloComercial,
        assessorado: translate('reuniaoInterna'),
        etapa: r.descricao,
        dtPrevista: dtPrevista,
        dtRealizadaInicio: dtRealizadaInicio,
        dtRealizadaFim: dtRealizadaFim,
        bu: r.bu,
        status: r.status,
        statusValidacao: r.statusValidacao,
        valorHora: r.valorHora,
        reuniaoInterna: true,
      };
    });

    const consolidado = [...resultadoTabela, ...internasTabela];

    setReunioesTabela(consolidado);
    setTotalPages(Math.ceil(consolidado.length / rowsPerPage));
  }, [resultado, reunioesInternas, rowsPerPage]);

  // Atualiza as linhas exibidas com base na paginação
  useEffect(() => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    setDisplayedRows(reunioesTabela.slice(start, end));
  }, [reunioesTabela, currentPage, rowsPerPage]);

  // Funções de navegação
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  const calcularDuracaoHoras = (inicio: Date | string, fim: Date | string): number => {
    const momentInicio = moment(inicio);
    const momentFim = moment(fim);
    const duration = moment.duration(momentFim.diff(momentInicio));
    return duration.asHours();
  };

  const getFormattedDataForExport = () => {
    const formatadores: Record<string, (params: { row: IReunioesTabela }) => string> = {
      dtPrevista: ({ row }) => formatarData(row.dtPrevista),
      dtRealizadaInicio: ({ row }) => formatarHora(row.dtRealizadaInicio),
      dtRealizadaFim: ({ row }) => formatarHora(row.dtRealizadaFim),
      horasConsumidas: ({ row }) => {
        const horas = calcularDuracaoHoras(row.dtRealizadaInicio, row.dtRealizadaFim);
        return `${horas.toFixed(2)} hrs`;
      },
      valorPago: ({ row }) => {
        const horas = calcularDuracaoHoras(row.dtRealizadaInicio, row.dtRealizadaFim);
        const valor = row.valorHora * horas;
        return `${translate('prefixoMoeda')} ${valor.toFixed(2)}`;
      },
      statusValidacao: ({ row }) => {
        const statusText = handleStatus(row.status, row.statusValidacao, translate);
        return statusText ?? translate('pendente');
      }
    };

    const modifiedColumns = columns.map(col => {
      const newCol = { ...col };

      if (formatadores[col.field]) {
        newCol.valueGetter = formatadores[col.field];
      }

      return newCol;
    });

    const columnsToInclude = modifiedColumns
      .filter(col => col.field !== '')
      .map(col => col.field);

    return {
      modifiedColumns,
      columnsToInclude,
      filename: `timesheet-${new Date().toISOString().split('T')[0]}`
    };
  };

  const handleExportToCSV = () => {
    const { modifiedColumns, columnsToInclude, filename } = getFormattedDataForExport();
    
    exportToCSV<IReunioesTabela>({
      rows: reunioesTabela,
      columns: modifiedColumns,
      includeColumns: columnsToInclude,
      additionalFields: [],
      filename,
    });
    
    setExportMenuAnchor(null);
  };

  const handleExportToXLSX = () => {
    const { modifiedColumns, columnsToInclude, filename } = getFormattedDataForExport();
    
    try {
      exportToXLSX<IReunioesTabela>({
        rows: reunioesTabela,
        columns: modifiedColumns,
        includeColumns: columnsToInclude,
        additionalFields: [],
        filename,
      });
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
    }
    
    setExportMenuAnchor(null);
  };

  const handleExportMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'unidade',
      headerName: translate('unidade'),
      width: 70,
      flex: 70,
    },
    {
      field: 'assessorado',
      headerName: translate('assessorado'),
      width: 160,
      flex: 160,
    },
    {
      field: 'etapa',
      headerName: translate('etapa'),
      width: 160,
      flex: 160,
    },
    {
      field: 'dtPrevista',
      headerName: translate('data'),
      width: 110,
      flex: 110,
      valueGetter: (params: any, row: any) => {
        const data = params as Date;
        return formatarData(data);
      },
      sortComparator: (v1, v2, param1, param2) => {
        // Pega os objetos de linha originais para comparar as datas
        const row1 = param1.api.getRow(param1.id) as IReunioesTabela;
        const row2 = param2.api.getRow(param2.id) as IReunioesTabela;

        // Extrai as datas e converte para timestamp para comparação
        const date1 = row1.dtPrevista instanceof Date ? row1.dtPrevista.getTime() : new Date(row1.dtPrevista || '').getTime();
        const date2 = row2.dtPrevista instanceof Date ? row2.dtPrevista.getTime() : new Date(row2.dtPrevista || '').getTime();

        return date1 - date2;
      },
    },
    {
      field: 'dtRealizadaInicio',
      headerName: translate('inicio'),
      width: 80,
      flex: 80,
      valueGetter: (params: any) => {
        const data = params as Date;
        return moment(data).format('HH:mm');
      },
    },
    {
      field: 'dtRealizadaFim',
      headerName: translate('fim'),
      width: 60,
      flex: 60,
      valueGetter: (params: any) => {
        const data = params as Date;
        return moment(data).format('HH:mm');
      },
    },
    {
      field: 'horasConsumidas',
      headerName: translate('horasConsumidas'),
      width: 90,
      flex: 90,
      valueGetter: (params: GridCellParams, row: any) => {
        const data = row as any;
        const inicio = moment(data.dtRealizadaInicio);
        const fim = moment(data.dtRealizadaFim);
        const duration = moment.duration(fim.diff(inicio));
        const horas = duration.asHours();
        return `${horas} hrs`;
      },
    },
    {
      field: 'valorPago',
      headerName: translate('valorPago'),
      width: 90,
      flex: 90,
      valueGetter: (params: GridCellParams, row: any) => {
        const data = row as any;
        const inicio = moment(data.dtRealizadaInicio);
        const fim = moment(data.dtRealizadaFim);
        const duration = moment.duration(fim.diff(inicio));
        const horas = duration.asHours();

        const valor = data.valorHora * horas;
        return `${translate('prefixoMoeda')} ${valor}`;
      },
    },
    {
      field: 'bu',
      headerName: translate('bu'),
      width: 110,
      flex: 110,
    },
    {
      field: 'statusValidacao',
      headerName: translate('status'),
      width: 150,
      flex: 150,
      editable: true,
      renderCell: (params: GridCellParams) => {
        const reuniao = params.row;
        return handleStatus(reuniao.status, reuniao.statusValidacao, translate);
      },
    },
  ];

  // Função para renderizar cards no mobile
  const renderMobileCard = (reuniao: IReunioesTabela, index: number) => {
    const horas = calcularDuracaoHoras(reuniao.dtRealizadaInicio, reuniao.dtRealizadaFim);
    const valor = reuniao.valorHora * horas;
    const statusText = handleStatus(reuniao.status, reuniao.statusValidacao, translate);

    return (
      <Card key={`${reuniao.id}-${index}`} sx={{ p: 2, mb: 2, borderRadius: '10px' }}>
        {/* Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Chip
            label={statusText}
            color="primary"
            size="small"
            sx={{
              fontSize: '0.75rem',
              maxWidth: '100%',
              height: 'auto',
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                overflow: 'hidden',
                padding: '4px 8px'
              }
            }}
          />
        </Box>

        {/* Títulos principais */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
            {reuniao.assessorado}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {reuniao.unidade}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {reuniao.bu || '-'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Etapa - ocupa linha inteira para evitar corte */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
            {translate("etapa")}
          </Typography>
          <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {reuniao.etapa || '-'}
          </Typography>
        </Box>

        {/* Detalhes em colunas */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {/* Coluna 1 */}
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
              {translate("data")}
            </Typography>
            <Typography variant="body2" component="div">
              {formatarData(reuniao.dtPrevista)}
            </Typography>

            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
              {translate("valorHora")}
            </Typography>
            <Typography variant="body2" component="div">
              {formatCurrency(reuniao.valorHora)}
            </Typography>
          </Box>

          {/* Coluna 2 */}
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 'bold' }}>
              {translate("inicio")}/{translate("fim")}
            </Typography>
            <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
              {formatarHora(reuniao.dtRealizadaInicio)} - {formatarHora(reuniao.dtRealizadaFim)}
            </Typography>

            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, fontWeight: 'bold' }}>
              {translate("horasConsumidas")}
            </Typography>
            <Typography variant="body2" component="div">
              {horas.toFixed(2)} hrs
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  return (
    <PaperRelatorio sx={{ mt: 3, height: '100%', width: '100%', boxSizing: 'border-box', maxWidth: '100%' }} elevation={3}>
      <Box
        sx={{
          width: '100%',
        }}
      >

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h3">{translate('resumo')}</Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleExportMenuClick}
            >
              {translate('exportar')}
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleExportToCSV}>
                {translate('exportarCSV')}
              </MenuItem>
              <MenuItem onClick={handleExportToXLSX}>
                {translate('exportarXLSX')}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="body1">
              <b>{translate('dataInicio')}</b>
            </Typography>
            <Typography variant="body2">
              {formatarData(dataInicio)}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1">
              <b>{translate('dataFim')}</b>
            </Typography>
            <Typography variant="body2">{formatarData(dataFim)}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1">
              <b>{translate('totalHoras')}</b>
            </Typography>
            <Typography variant="body2">{totalHoras} hrs</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body1">
              <b>{translate('totalReceber')}</b>
            </Typography>
            <Typography variant="body2">{formatCurrency(totalReceber)}</Typography>
          </Grid>
        </Grid>

        {isMobile ? (
          <Box sx={{ mt: 3 }}>
            {reunioesTabela.length === 0 ? (
              <Typography variant="body1" align="center" sx={{ py: 4 }}>
                {translate('nenhumRegistroEncontrado')}
              </Typography>
            ) : (
              <>
                {displayedRows.map((reuniao, index) => renderMobileCard(reuniao, index))}

                {/* Controles de paginação */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0}
                      size="small"
                    >
                      &lt; {translate('anterior')}
                    </Button>

                    <Typography variant="body2">
                      {translate('exibindo')} {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, reunioesTabela.length)} {translate('de')} {reunioesTabela.length}
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1}
                      size="small"
                    >
                      {translate('proximo')} &gt;
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : (
          <ThemeProvider theme={createTheme({}, locale)}>
            <DataGrid
              rows={reunioesTabela}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'dtPrevista', sort: 'desc' }],
                },
              }}
              sx={{
                backgroundColor: 'white',
                borderRadius: '20px',
                height: '500px',
                marginTop: '15px',
                width: '100%',
                '& .MuiDataGrid-main': { width: '100%' },
                '& .MuiDataGrid-virtualScroller': { width: '100%' },
                '& .MuiDataGrid-columnsContainer': { width: '100%' },
              }}
            />
          </ThemeProvider>
        )}
      </Box>
    </PaperRelatorio>
  );
};

const SpanValidar = styled.span`
  font-size: 12px;
  cursor: pointer;
`;

export default TimesheetRestultado;
